import React, { useCallback, useMemo, useState } from 'react'
import { xor } from 'lodash'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useMediaQuery, useTheme } from '@mui/material'
import { useFetchChargingCards } from '../../api/use-fetch-charging-cards'
import { ChargingToWallboxMobileView } from './mobile/ChargingToWallboxMobileView'
import { ChargingCardToWallboxLinkDesktopView } from './desktop/ChargingCardToWallboxLinkDesktopView'
import { useForm } from 'react-hook-form'
import { useAssignToken } from '../../../charging-cards/api/use-assign-token'
import { AssignTokenLinkDataDto } from '../../api/dto/assign-token-dto'
import { ChargingCardToWallboxFormData } from './charging-card-to-wallbox-form-data'

export interface ChargingCardToWallboxLinkDialogProps {
  installationId: number
  open: boolean
  onClose: () => void
  place: number
}

/**
 * Dialog component for charging card link to wallboxes
 * Inside dialog we have separation for mobile and desktop views
 */
export const ChargingCardToWallboxLinkDialog: React.FC<ChargingCardToWallboxLinkDialogProps> = ({ installationId, open, onClose, place }) => {
  const { chargingCards, wallboxes } = useFetchChargingCards(Number(installationId))

  const isManagedMode = chargingCards.some(card => card.linkedWallboxes.length > 0)

  const [search, setSearch] = useState('')
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const filteredChargingCards = useMemo(
    () =>
      chargingCards.filter(
        card =>
          card.cardName?.toLowerCase().includes(search.toLocaleLowerCase()) ||
          card.user?.toLowerCase().includes(search.toLocaleLowerCase()) ||
          card.userId?.toLowerCase().includes(search.toLocaleLowerCase()) ||
          card.tokenId.toLowerCase().includes(search.toLocaleLowerCase())
      ),
    [chargingCards, search]
  )

  /**
   * Keep default values initialized in a separate variable instead of writing these fields inside useForm argument directly.
   * This way, we enforce that ALL fields must have a default value since useForm({ defaultValues: ... }) allows fields of FormData
   * to be optional.
   *
   * Default values are populated like so:
   *
   * {
   *   "chargingCardId1": {
   *     "wallboxId1": true,
   *     "wallboxId2": false,
   *     "wallboxId3": true,
   *     ...
   *   },
   *   "chargingCardId2": {
   *     ...
   *   },
   *   ...
   * }
   *
   * Each key at the top level corresponds to a single charging card. Then, inside each of the top-level keys, there is
   * a map if each of the wallboxes is connected to the card or not.
   *
   */
  const defaultValues = useMemo<ChargingCardToWallboxFormData>(() => {
    const result: ChargingCardToWallboxFormData = {}

    for (const chargingCard of chargingCards) {
      const wallboxMap: Record<string, boolean> = {}

      for (const wallbox of wallboxes) {
        const isLinkedToCurrentCard = chargingCard.linkedWallboxes.some(linkedWallbox => linkedWallbox.wallboxId === wallbox.wallboxId)

        wallboxMap[wallbox.wallboxId] = isLinkedToCurrentCard
      }

      result[chargingCard.ocpiTokenId] = wallboxMap
    }

    return result
  }, [chargingCards, wallboxes])

  const { control, formState, reset, getValues } = useForm<ChargingCardToWallboxFormData>({
    defaultValues,
  })

  const handleSearchChange = useCallback((param: string) => {
    setSearch(param)
  }, [])

  const { assignToken, isLoading: isAssignTokenLoading } = useAssignToken(Number(installationId), newData => {
    /**
     * On successful assigning of wallboxes to a charging card, we will get the edited charging card in the response.
     *
     * To make sure we're in sync with the data in the database after the edit and to avoid fetching all charging cards again,
     * we use the response data and reset the form to these new values so that we can detect the new dirty state on the updated
     * data.
     */
    reset(
      Object.fromEntries(
        chargingCards.map(card => {
          const selectedIds =
            card.ocpiTokenId === newData.ocpi_token_id ? newData.wallboxes ?? [] : card.linkedWallboxes.map(wallbox => wallbox.wallboxId)

          return [card.ocpiTokenId, Object.fromEntries(wallboxes.map(wallbox => [wallbox.wallboxId, selectedIds.includes(wallbox.wallboxId)]))]
        })
      )
    )
  })

  /**
   * Because dirtying one checkbox in one row should disable all other rows, we cannot do this check at the Controller
   * level. Controller isolates re-renders to this single field. For this reason, we listen to any form change at the
   * top of the component.
   */
  const dirtyOCPITokenId = useMemo(() => Object.keys(formState.dirtyFields).find(ocpiTokenId => ocpiTokenId), [formState])

  const disabledOcpiTokenIds = useMemo(() => {
    /** if network request is ongoing, all rows should be disabled */
    if (isAssignTokenLoading) {
      return chargingCards.map(chargingCard => chargingCard.ocpiTokenId)
    }

    /** if user did not click any checkbox yet, no rows are disabled */
    if (dirtyOCPITokenId === undefined) {
      // nothing is dirty → nothing disabled
      return []
    }

    /** if there is a dirty row detected, disable all except the dirty one to make sure only a single API call can be made at a time */
    return xor(
      chargingCards.map(chargingCard => chargingCard.ocpiTokenId),
      [dirtyOCPITokenId]
    )
  }, [chargingCards, dirtyOCPITokenId, isAssignTokenLoading])

  const handleSubmit = useCallback(() => {
    if (dirtyOCPITokenId === undefined) {
      return
    }

    const rowValues = getValues()[dirtyOCPITokenId]
    const assignedWallboxIds = Object.keys(rowValues).filter(wallboxId => rowValues[wallboxId])

    const payload: AssignTokenLinkDataDto = {
      wallboxes: assignedWallboxIds,
      installationId: installationId,
      tokenId: dirtyOCPITokenId,
    }

    assignToken(payload)
  }, [installationId, dirtyOCPITokenId, getValues, assignToken])

  const handleCancelButtonClick = useCallback(() => {
    reset()
  }, [reset])

  return (
    <Dialog maxWidth="lg" title={t('emobility.chargingCards.link-charging-cards-and-wallboxes-dialog-title')} onClose={onClose} place={place}>
      {isMobile ? (
        <ChargingToWallboxMobileView
          wallboxes={wallboxes}
          installationId={installationId}
          control={control}
          chargingCards={filteredChargingCards}
          dirtyOCPITokenId={dirtyOCPITokenId}
          disabledOCPITokenIds={disabledOcpiTokenIds}
          isAssignTokenLoading={isAssignTokenLoading}
          onSubmit={handleSubmit}
          onCancelButtonClick={handleCancelButtonClick}
          search={search}
          onSearchChange={handleSearchChange}
          reset={reset}
          isManagedMode={isManagedMode}
        />
      ) : (
        <ChargingCardToWallboxLinkDesktopView
          wallboxes={wallboxes}
          installationId={installationId}
          control={control}
          chargingCards={filteredChargingCards}
          search={search}
          dirtyOCPITokenId={dirtyOCPITokenId}
          disabledOCPITokenIds={disabledOcpiTokenIds}
          isAssignTokenLoading={isAssignTokenLoading}
          onSubmit={handleSubmit}
          onCancelButtonClick={handleCancelButtonClick}
          onSearchChange={handleSearchChange}
          reset={reset}
          isManagedMode={isManagedMode}
        />
      )}
    </Dialog>
  )
}
